import React from 'react';
import { humanizeKey } from '../../utils/humanizeKey';
import { hasMultipleOptions, getComponentOptions, getOptionTotal } from '../Quotes/optionUtils';
import { 
  Anchor,
  Ship,
  Wine,
  Utensils,
  Sparkles,
  MapPin,
  Calendar,
  Users,
  Crown,
  Star,
  CheckCircle2,
  Mail,
  Phone
} from 'lucide-react';
import { TEMPLATE_PALETTES } from './templatePalettes';

// Voyager Pro / Figma reskin - 'luxury_river' is a navy/bronze palette in
// Figma, replacing the previous amber/gold cruise look. Full recolor.
const PALETTE = TEMPLATE_PALETTES.luxury_river;

const LuxuryRiverTemplate = ({ quote, onPayment, isAgentPreview = false }) => {
  if (!quote) {
    return null;
  }

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Helper function to format dates elegantly
  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Parse date in local timezone to avoid UTC offset issues
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get client name
  const getClientName = () => {
    if (quote.client?.firstName && quote.client?.lastName) {
      return `${quote.client.firstName} ${quote.client.lastName}`;
    }
    return 'Distinguished Guest';
  };

  // Extract ship and cabin information from components
  const getCruiseDetails = () => {
    const cruiseComponent = quote.components?.find(c => 
      c.type?.toLowerCase().includes('cruise') || 
      c.title?.toLowerCase().includes('cruise')
    );
    
    if (cruiseComponent?.data) {
      return {
        shipName: cruiseComponent.data.ship_name || cruiseComponent.data.vessel_name || null,
        cabinType: cruiseComponent.data.cabin_type || cruiseComponent.data.stateroom_type || null,
        cruiseLine: cruiseComponent.data.cruise_line || null,
        duration: cruiseComponent.data.duration || cruiseComponent.data.nights || null
      };
    }
    return {};
  };

  const cruiseDetails = getCruiseDetails();

  return (
    <div className="min-h-screen" style={{ ...PALETTE, backgroundColor: 'var(--background)' }}>
      {/* Elegant Header */}
      <div className="bg-[var(--muted)] border-b border-[var(--border)]">
        <div className="max-w-6xl mx-auto px-8 py-12 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Anchor className="w-8 h-8 text-[var(--accent)]" />
            <span className="text-[var(--accent)] font-serif text-sm tracking-widest uppercase">Luxury River Voyage</span>
            <Anchor className="w-8 h-8 text-[var(--accent)]" />
          </div>
          
          <h1 className="font-serif text-5xl md:text-6xl text-[var(--foreground)] mb-4 tracking-wide">
            {quote.quoteTitle || 'Your River Journey'}
          </h1>
          
          <p className="text-[var(--accent)] text-lg font-light tracking-wide">
            Prepared for {getClientName()}
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-8 py-16">
        
        {/* Ship & Amenities Section - Top Half */}
        <div className="mb-20">
          <div className="bg-white/70 backdrop-blur-sm rounded-lg border border-[var(--border)] shadow-lg p-12">
            <div className="text-center mb-12">
              <Ship className="w-16 h-16 text-[var(--accent)] mx-auto mb-6" />
              <h2 className="font-serif text-4xl text-[var(--foreground)] mb-2">Your Vessel</h2>
              <div className="w-24 h-0.5 bg-[var(--accent)]/40 mx-auto"></div>
            </div>

            {/* Ship Details Grid */}
            <div className="grid md:grid-cols-2 gap-12 mb-12">
              {cruiseDetails.shipName && (
                <div className="text-center p-8 bg-[var(--muted)] rounded-lg border border-[var(--border)]">
                  <Crown className="w-10 h-10 text-[var(--accent)] mx-auto mb-4" />
                  <div className="text-sm text-[var(--accent)] uppercase tracking-widest mb-2">Vessel Name</div>
                  <div className="font-serif text-3xl text-[var(--foreground)]">{cruiseDetails.shipName}</div>
                </div>
              )}
              
              {cruiseDetails.cabinType && (
                <div className="text-center p-8 bg-[var(--muted)] rounded-lg border border-[var(--border)]">
                  <Star className="w-10 h-10 text-[var(--accent)] mx-auto mb-4" />
                  <div className="text-sm text-[var(--accent)] uppercase tracking-widest mb-2">Accommodation</div>
                  <div className="font-serif text-3xl text-[var(--foreground)]">{cruiseDetails.cabinType}</div>
                </div>
              )}
            </div>

            {/* Voyage Details */}
            <div className="grid md:grid-cols-3 gap-8 pt-8 border-t border-[var(--border)]">
              {quote.travelDates?.start && (
                <div className="text-center">
                  <Calendar className="w-6 h-6 text-[var(--accent)] mx-auto mb-3" />
                  <div className="text-xs text-[var(--accent)] uppercase tracking-widest mb-2">Embarkation</div>
                  <div className="text-[var(--foreground)] font-light">{formatDate(quote.travelDates.start)}</div>
                </div>
              )}
              
              {quote.travelDates?.end && (
                <div className="text-center">
                  <Calendar className="w-6 h-6 text-[var(--accent)] mx-auto mb-3" />
                  <div className="text-xs text-[var(--accent)] uppercase tracking-widest mb-2">Disembarkation</div>
                  <div className="text-[var(--foreground)] font-light">{formatDate(quote.travelDates.end)}</div>
                </div>
              )}
              
              {(quote.travelers?.adults || quote.travelers?.children?.length) && (
                <div className="text-center">
                  <Users className="w-6 h-6 text-[var(--accent)] mx-auto mb-3" />
                  <div className="text-xs text-[var(--accent)] uppercase tracking-widest mb-2">Guests</div>
                  <div className="text-[var(--foreground)] font-light">
                    {quote.travelers.adults || 0} Adult{quote.travelers.adults !== 1 ? 's' : ''}
                    {quote.travelers.children?.length > 0 && `, ${quote.travelers.children.length} Child${quote.travelers.children.length !== 1 ? 'ren' : ''}`}
                  </div>
                </div>
              )}
            </div>

            {/* Amenities */}
            {cruiseDetails.cruiseLine && (
              <div className="mt-12 pt-12 border-t border-[var(--border)] text-center">
                <div className="inline-flex items-center gap-3 px-8 py-4 bg-[var(--muted)] rounded-full border border-[var(--border)]">
                  <Sparkles className="w-5 h-5 text-[var(--accent)]" />
                  <span className="text-[var(--foreground)] font-serif text-xl">{cruiseDetails.cruiseLine}</span>
                  <Sparkles className="w-5 h-5 text-[var(--accent)]" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Ports of Call Section - Bottom Half */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl text-[var(--foreground)] mb-4">Ports of Call</h2>
            <p className="text-[var(--accent)] font-light tracking-wide">Your curated journey along the river</p>
            <div className="w-24 h-0.5 bg-[var(--accent)]/40 mx-auto mt-4"></div>
          </div>

          {quote.components && quote.components.length > 0 ? (
            <div className="space-y-6">
              {quote.components.map((component, index) => (
                <div
                  key={component.id || index}
                  className="bg-white/70 backdrop-blur-sm rounded-lg border border-[var(--border)] shadow-sm hover:shadow-md transition-shadow p-8"
                >
                  <div className="flex items-start justify-between gap-6">
                    <div className="flex items-start gap-6 flex-1">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[var(--muted)] to-[var(--muted)] border border-[var(--border)] flex items-center justify-center">
                          <span className="font-serif text-[var(--foreground)] font-semibold">{index + 1}</span>
                        </div>
                      </div>
                      
                      <div className="flex-1">
                        <div className="mb-3">
                          <span className="inline-block px-3 py-1 bg-[var(--muted)] text-[var(--accent)] text-xs uppercase tracking-widest rounded-full mb-2">
                            {component.type || 'Experience'}
                          </span>
                        </div>
                        
                        <h3 className="font-serif text-2xl text-[var(--foreground)] mb-4">
                          {component.title || `Stop ${index + 1}`}
                        </h3>

                        {/* Component Details */}
                        {component.data && Object.keys(component.data).length > 0 && (
                          <div className="grid md:grid-cols-2 gap-4 mt-4">
                            {Object.entries(component.data).map(([key, value]) => {
                              if (!value || key === 'isOption' || key === 'optionDescription') return null;
                              
                              // Check if this is a price field
                              const isPriceField = key.toLowerCase().includes('price') || 
                                                   key.toLowerCase().includes('cost') || 
                                                   key.toLowerCase().includes('fee') ||
                                                   key.toLowerCase().includes('total') ||
                                                   key.toLowerCase().includes('deposit');
                              const isNumeric = !isNaN(parseFloat(value)) && isFinite(value);
                              
                              let displayValue = String(value);
                              if (isPriceField && isNumeric) {
                                displayValue = formatCurrency(parseFloat(value));
                              }
                              
                              return (
                                <div key={key} className="flex items-start gap-2">
                                  <MapPin className="w-4 h-4 text-[var(--accent)] mt-1 flex-shrink-0" />
                                  <div>
                                    <div className="text-xs text-[var(--accent)] uppercase tracking-wide mb-1">
                                      {humanizeKey(key)}
                                    </div>
                                    <div className="text-[var(--foreground)] font-light">
                                      {displayValue}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Component Photos */}
                        {component.photos && component.photos.length > 0 && (
                          <div className="mt-6 pt-6 border-t border-[var(--border)]">
                            <h4 className="text-sm font-serif font-bold text-[var(--foreground)] mb-3 flex items-center gap-2">
                              <span>📸</span> Gallery
                            </h4>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                              {component.photos.map((photo, photoIndex) => (
                                <div 
                                  key={photoIndex} 
                                  className="relative group cursor-pointer rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow border-2 border-[var(--border)] bg-white"
                                  onClick={() => window.open(photo.url, '_blank')}
                                >
                                  <img 
                                    src={photo.url} 
                                    alt={photo.name || 'Component photo'} 
                                    className="w-full h-auto object-contain"
                                    style={{ maxHeight: '300px' }}
                                  />
                                  <div className="absolute inset-0 bg-gradient-to-t from-[var(--primary)]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                                    <span className="text-[var(--background)] text-sm font-semibold">{photo.name || 'View Photo'}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Component Documents */}
                        {component.documents && component.documents.length > 0 && (
                          <div className="mt-4">
                            <h4 className="text-sm font-serif font-bold text-[var(--foreground)] mb-2 flex items-center gap-2">
                              <span>📋</span> Documents
                            </h4>
                            <div className="space-y-2">
                              {component.documents.map((doc, docIndex) => (
                                <a 
                                  key={docIndex}
                                  href={doc.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-3 bg-[var(--muted)] hover:brightness-95 px-4 py-2 rounded-xl transition-colors border border-[var(--border)]"
                                >
                                  <span className="text-lg">📄</span>
                                  <span className="text-[var(--foreground)] text-sm font-semibold">{doc.name}</span>
                                </a>
                              ))}
                            </div>
                          </div>
                        )}

                        {hasMultipleOptions(component) && (
                          <div className="mt-6" style={{ paddingLeft: '1rem', paddingRight: '1rem', paddingBottom: '1rem' }}>
                            <h4 className="text-sm font-bold mb-3 uppercase tracking-wide" style={{ color: 'var(--foreground)' }}>Choose Your Option</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              {getComponentOptions(component).map((option, optIndex) => (
                                <div key={option.id || optIndex} className="rounded-xl overflow-hidden" style={{ border: `2px solid ${optIndex === 0 ? 'var(--accent)' : 'var(--border)'}` }}>
                                  {option.photoUrl && (
                                    <img src={option.photoUrl} alt={option.label || 'Option'} className="w-full h-28 object-cover" />
                                  )}
                                  <div className="p-4">
                                    <div className="flex items-center justify-between gap-2 mb-1">
                                      <span className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>{option.label || `Option ${optIndex + 1}`}</span>
                                      {option.badge && (
                                        <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: 'var(--muted)', color: 'var(--accent)' }}>{option.badge}</span>
                                      )}
                                    </div>
                                    {option.description && (
                                      <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>{option.description}</p>
                                    )}
                                    <p className="text-lg font-bold" style={{ color: 'var(--accent)' }}>{formatCurrency(getOptionTotal(option))}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="text-right flex-shrink-0">
                      <div className="text-xs text-[var(--accent)] uppercase tracking-widest mb-2">Investment</div>
                      <div className="font-serif text-2xl text-[var(--foreground)]">
                        {formatCurrency(component.price)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white/70 backdrop-blur-sm rounded-lg border border-[var(--border)] p-12 text-center">
              <Ship className="w-16 h-16 text-[var(--border)] mx-auto mb-4" />
              <p className="text-[var(--accent)] font-light">No ports of call listed</p>
            </div>
          )}
        </div>

        {/* Special Notes */}
        {quote.notes && (
          <div className="mb-20 bg-[var(--muted)] rounded-lg border border-[var(--border)] p-10">
            <div className="flex items-center gap-3 mb-6">
              <Wine className="w-6 h-6 text-[var(--accent)]" />
              <h3 className="font-serif text-2xl text-[var(--foreground)]">Voyage Notes</h3>
            </div>
            <p className="text-[var(--foreground)] font-light leading-relaxed whitespace-pre-wrap">
              {quote.notes}
            </p>
          </div>
        )}

        {/* Investment Summary - Elegant Box */}
        <div className="bg-[var(--muted)] rounded-lg border-2 border-[var(--border)] shadow-xl overflow-hidden">
          <div className="p-12">
            <div className="text-center mb-10">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="w-16 h-0.5 bg-[var(--accent)]/40"></div>
                <Crown className="w-10 h-10 text-[var(--accent)]" />
                <div className="w-16 h-0.5 bg-[var(--accent)]/40"></div>
              </div>
              
              <h3 className="font-serif text-3xl text-[var(--foreground)] mb-6">Total Investment</h3>
              <div className="font-serif text-6xl text-[var(--foreground)] mb-2">
                {formatCurrency(quote.totalPrice)}
              </div>
              <p className="text-[var(--accent)] font-light tracking-wide">Per voyage, all-inclusive</p>
            </div>

            {quote.depositAmount > 0 && (
              <div className="border-t border-[var(--border)] pt-10 mb-10">
                <div className="grid md:grid-cols-2 gap-8 text-center">
                  <div>
                    <div className="text-xs text-[var(--accent)] uppercase tracking-widest mb-3">Initial Deposit</div>
                    <div className="font-serif text-3xl text-[var(--foreground)]">{formatCurrency(quote.depositAmount)}</div>
                  </div>
                  {quote.depositDueDate && (
                    <div>
                      <div className="text-xs text-[var(--accent)] uppercase tracking-widest mb-3">Due Date</div>
                      <div className="text-xl text-[var(--foreground)] font-light">{formatDate(quote.depositDueDate)}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Payment Button */}
            {onPayment && (
              <div>
                <button
                  onClick={onPayment}
                  disabled={isAgentPreview}
                  className={`w-full bg-[var(--primary)] hover:brightness-110 text-[var(--background)] font-serif py-5 px-8 rounded-lg text-lg transition-all duration-300 flex items-center justify-center gap-3 ${
                    isAgentPreview ? 'opacity-50 cursor-not-allowed' : 'shadow-lg hover:shadow-xl'
                  }`}
                >
                  <CheckCircle2 className="w-5 h-5" />
                  {isAgentPreview ? 'Preview Mode' : 'Reserve Your Voyage'}
                  <Sparkles className="w-5 h-5" />
                </button>
                {!isAgentPreview && (
                  <p className="text-center text-xs text-[var(--accent)] mt-6 tracking-wide">
                    Secure booking • Concierge service included
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Agent Contact Footer */}
        {(quote.agent_name || quote.agent_email || quote.agent_phone) && (
          <div className="mt-12 bg-[var(--muted)] rounded-2xl border-2 border-[var(--border)] overflow-hidden shadow-xl">
            <div className="bg-[var(--primary)] px-6 py-4">
              <div className="flex items-center justify-center gap-3 text-[var(--background)]">
                <Anchor className="w-6 h-6" />
                <h3 className="text-lg font-serif font-bold">Your Luxury Travel Curator</h3>
              </div>
            </div>
            
            <div className="p-6">
              {quote.agent_name && (
                <div className="text-center mb-4">
                  <p className="text-xl font-serif font-bold text-[var(--foreground)]">{quote.agent_name}</p>
                  {quote.agent_business_name && (
                    <p className="text-[var(--accent)] font-semibold">{quote.agent_business_name}</p>
                  )}
                </div>
              )}
              
              <div className="space-y-3 max-w-md mx-auto">
                {quote.agent_email && (
                  <a 
                    href={`mailto:${quote.agent_email}`}
                    className="flex items-center gap-3 bg-white px-4 py-3 rounded-xl hover:bg-[var(--muted)] transition-colors border border-[var(--border)] shadow-sm"
                  >
                    <Mail className="w-5 h-5 text-[var(--primary)]" />
                    <span className="text-[var(--foreground)] font-medium">{quote.agent_email}</span>
                  </a>
                )}
                
                {quote.agent_phone && (
                  <a 
                    href={`tel:${quote.agent_phone}`}
                    className="flex items-center gap-3 bg-white px-4 py-3 rounded-xl hover:bg-[var(--muted)] transition-colors border border-[var(--border)] shadow-sm"
                  >
                    <Phone className="w-5 h-5 text-[var(--primary)]" />
                    <span className="text-[var(--foreground)] font-medium">{quote.agent_phone}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-16 text-center">
          <div className="flex items-center justify-center gap-3 text-[var(--accent)]/60">
            <div className="w-12 h-0.5 bg-[var(--border)]"></div>
            <Anchor className="w-5 h-5" />
            <span className="text-sm font-serif tracking-widest">Luxury River Cruising</span>
            <Anchor className="w-5 h-5" />
            <div className="w-12 h-0.5 bg-[var(--border)]"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LuxuryRiverTemplate;
